import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Typography } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import { red } from '@material-ui/core/colors';
import { Elements, StripeProvider } from 'react-stripe-elements';
import RenewalPaymentForm from './RenewalPaymentForm';
import VoucherForm from './VoucherForm';
import ContentHeader from './ContentHeader';
import {
    formatTicketType,
    formatDate,
    formatPrice
} from '../helpers/formatters';
import { getYearGroups } from '../helpers/ticket';
import ZonesForm from './ZonesForm';
import globalStyles from '../styles';
import { waitForObjectElement } from '../helpers/wait';

const styles = {
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    error: {
        margin: '20px 20px 10px',
        padding: '20px',
        color: red[500],
        textAlign: 'center'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    zonesDescription: {
        padding: '20px 0 0',
        fontWeight: '100',
        textAlign: 'center'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    zoneMenuItem: {
        whiteSpace: 'normal'
    },
    formControl: {
        maxWidth: 250,
        width: '100%',
        margin: '0 auto',
        display: 'flex'
    },
    zonesForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        paddingBottom: '35px'
    },
    link: globalStyles.link
};

class ConfirmRenewal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null,
            stop: props.stop,
            yr: props.yr,
            dest: props.dest,
            zone: props.zone,
            zoneStop: props.zoneStop,
            extId: props.extId,
            stripe: null
        };
    }

    async componentDidMount() {
        const { client } = this.props;

        try {
            // Wait for stripe to be loaded
            await waitForObjectElement(window, 'Stripe');

            const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUB_KEY, {
                stripeAccount: client.stripe_account_id
            });

            this.setState({
                stripe
            });
        } catch (error) {
            console.error('Error loading Stripe:', error);
            this.setState({
                error:
                    'There was an error loading the payment system. Please try again later or contact support.'
            });
        }
    }

    setVoucher = voucher => {
        this.setState({ voucher });
    };

    netPrice = price => {
        const { voucher } = this.state;
        if (voucher) {
            return price - voucher.value;
        }
        return price;
    };

    onBusStopChange = event => {
        this.setState({
            stop: event.target.value
        });
    };

    onYearGroupChange = event => {
        this.setState({
            yr: event.target.value
        });
    };

    onDestinationChange = event => {
        this.setState({
            dest: event.target.value
        });
    };

    onExtIdChange = event => {
        this.setState({
            extId: event.target.value
        });
    };

    render() {
        const {
            passenger,
            ticket,
            client,
            service,
            renewal,
            classes,
            requireStudentId
        } = this.props;

        const {
            voucher,
            stop,
            yr,
            dest,
            zone,
            zoneStop,
            stripe,
            extId,
            error
        } = this.state;

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        if (!stripe) {
            return <></>;
        }

        let yearGroups = [];

        if (service) {
            yearGroups = getYearGroups(service.yr_groups);
        }

        const showDestination =
            service && service.destinations && service.destinations.length > 0;

        const useZones =
            service.zones &&
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices;

        const formattedPrice = formatPrice(
            this.netPrice(useZones ? ticket.zone_prices[zone] : ticket.price),
            ticket.currency
        );

        return (
            <StripeProvider stripe={stripe}>
                <div>
                    <div className={classes.root}>
                        <ContentHeader
                            text="Renew ticket"
                            id="ticket-details-heading"
                        />
                        <div
                            className={classes.passengerInfo}
                            style={{ marginBottom: 0 }}
                        >
                            <Typography variant="body2">
                                Passenger: {passenger.first_name}{' '}
                                {passenger.last_name}
                            </Typography>
                            <Typography variant="body2">
                                Ticket:{' '}
                                {formatTicketType(
                                    service.name,
                                    ticket.type,
                                    ticket.sub_type,
                                    ticket.travel_pattern,
                                    ticket.name
                                )}{' '}
                                {ticket.year}
                            </Typography>
                            <Typography variant="body2">
                                Valid from: {formatDate(ticket.start)}
                            </Typography>
                            <Typography variant="body2">
                                Valid until: {formatDate(ticket.expiry)}
                            </Typography>
                            {formattedPrice !== '' && (
                                <Typography variant="body2">
                                    Price: {formattedPrice}
                                </Typography>
                            )}
                        </div>
                        <div
                            style={{
                                background: '#fdfdcf',
                                // maxWidth: 600,
                                margin: '0 auto 20px',
                                padding: 10,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="body2">
                                Need a different ticket type? Contact{' '}
                                {client.name} to discuss your renewal
                                preference.
                            </Typography>
                        </div>
                    </div>
                    {requireStudentId && (
                        <div
                            className={classes.root}
                            style={{ marginBottom: 20 }}
                        >
                            <ContentHeader
                                text="Student ID"
                                id="student-id-heading"
                            />
                            <div style={{ padding: '35px 0' }}>
                                <FormControl
                                    variant="outlined"
                                    className={classes.formControl}
                                >
                                    <TextField
                                        autoComplete="off"
                                        className={`${classes.input} input`}
                                        id="id"
                                        name="id"
                                        type="text"
                                        label="Student ID"
                                        value={extId}
                                        onChange={this.onExtIdChange}
                                        variant="outlined"
                                    />
                                </FormControl>
                                <Typography
                                    className={classes.zonesDescription}
                                    variant="body2"
                                >
                                    <a
                                        href="https://help.shuttleid.uk/en/articles/7949374-how-to-find-my-student-id"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.link}
                                    >
                                        Help finding your student id
                                    </a>
                                </Typography>
                            </div>
                        </div>
                    )}
                    {useZones && (
                        <div
                            className={classes.root}
                            style={{ marginBottom: 30 }}
                        >
                            <ContentHeader
                                text="Zone selection"
                                id="availability-heading"
                            />
                            <Typography
                                className={classes.zonesDescription}
                                variant="body2"
                            >
                                Select your travel zone.{' '}
                                {service.timetable_url && (
                                    <>
                                        <a
                                            href={service.timetable_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={classes.link}
                                        >
                                            Help finding your zone
                                        </a>
                                        .
                                    </>
                                )}
                            </Typography>
                            <div className={classes.zonesForm}>
                                <ZonesForm
                                    classes={classes}
                                    passenger={passenger}
                                    service={service}
                                    ticket={ticket}
                                    handleChange={e => {
                                        this.setState({
                                            [e.target.name]: e.target.value
                                        });
                                    }}
                                    zone={zone}
                                    zoneStop={zoneStop}
                                />
                            </div>
                        </div>
                    )}
                    {service && 'stops' in service && !useZones && (
                        <div
                            className={classes.root}
                            style={{ marginBottom: 20 }}
                        >
                            <ContentHeader text="Bus stop" id="stop-heading" />
                            <div style={{ padding: '35px 0' }}>
                                <FormControl
                                    variant="outlined"
                                    className={classes.formControl}
                                >
                                    <InputLabel style={{ background: 'white' }}>
                                        Bus Stop
                                    </InputLabel>
                                    <Select
                                        id="stop"
                                        name="stop"
                                        value={stop}
                                        onChange={this.onBusStopChange}
                                        variant="outlined"
                                        input={
                                            <OutlinedInput
                                                name="stop"
                                                labelWidth={0}
                                                id="outlined-stop"
                                            />
                                        }
                                    >
                                        {service.stops.map(serviceStop => (
                                            <MenuItem
                                                key={`stop-${serviceStop}`}
                                                value={serviceStop}
                                            >
                                                {serviceStop}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                    )}

                    {showDestination && (
                        <div
                            className={classes.root}
                            style={{ marginBottom: 20 }}
                        >
                            <ContentHeader
                                text="Destination"
                                id="dest-heading"
                            />
                            <div style={{ padding: '35px 0' }}>
                                <FormControl
                                    variant="outlined"
                                    className={classes.formControl}
                                >
                                    <InputLabel style={{ background: 'white' }}>
                                        Destination
                                    </InputLabel>
                                    <Select
                                        id="dest"
                                        name="dest"
                                        value={dest}
                                        onChange={this.onDestinationChange}
                                        variant="outlined"
                                        input={
                                            <OutlinedInput
                                                name="dest"
                                                labelWidth={0}
                                                id="outlined-dest"
                                            />
                                        }
                                    >
                                        {service.destinations.map(
                                            serviceDest => (
                                                <MenuItem
                                                    key={`dest-${serviceDest}`}
                                                    value={serviceDest}
                                                >
                                                    {serviceDest}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                    )}

                    {yearGroups.length > 0 && (
                        <div
                            className={classes.root}
                            style={{ marginBottom: 20 }}
                        >
                            <ContentHeader text="Year group" id="yr-heading" />
                            <div style={{ padding: '35px 0' }}>
                                <FormControl
                                    variant="outlined"
                                    className={classes.formControl}
                                >
                                    <InputLabel style={{ background: 'white' }}>
                                        Year group
                                    </InputLabel>
                                    <Select
                                        id="yr"
                                        name="yr"
                                        value={yr}
                                        onChange={this.onYearGroupChange}
                                        variant="outlined"
                                        input={
                                            <OutlinedInput
                                                name="yr"
                                                labelWidth={0}
                                                id="outlined-yr"
                                            />
                                        }
                                    >
                                        {yearGroups.map(year => (
                                            <MenuItem
                                                key={`year-${year}`}
                                                value={year}
                                            >
                                                {year === 'reception'
                                                    ? 'Reception'
                                                    : `Year ${year}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                    )}

                    <div className={classes.root}>
                        <ContentHeader text="Voucher" id="voucher-heading" />
                        <VoucherForm
                            client={client}
                            setVoucher={this.setVoucher}
                        />
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Pay" id="pay-heading" />
                        <Elements
                            fonts={[
                                {
                                    cssSrc:
                                        'https://fonts.googleapis.com/css?family=Roboto:400,500'
                                }
                            ]}
                        >
                            <RenewalPaymentForm
                                ticket={ticket}
                                service={service}
                                passenger={passenger}
                                client={client}
                                voucher={voucher}
                                renewal={renewal}
                                stop={stop}
                                yr={yr}
                                zone={zone}
                                zoneStop={zoneStop}
                                dest={dest}
                                extId={extId}
                            />
                        </Elements>
                    </div>
                </div>
            </StripeProvider>
        );
    }
}

ConfirmRenewal.propTypes = {
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string
    }).isRequired,
    ticket: PropTypes.shape({
        start: PropTypes.string,
        expiry: PropTypes.string,
        price: PropTypes.string
    }).isRequired,
    client: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    classes: PropTypes.shape().isRequired,
    dest: PropTypes.string,
    requireStudentId: PropTypes.bool
};

ConfirmRenewal.defaultProps = {
    dest: '',
    requireStudentId: false
};

export default withStyles(styles)(ConfirmRenewal);
